// src/client/components/Header.jsx
import React, { useEffect, useMemo, useState } from 'react';
import useScrollSpy from '../../core/hooks/useScrollSpy';
import clientConfig from '../config/clientConfig';

// (optionnels selon offre Commande)
import { useCart } from '../../core/context/CartContext';
import { useAuthClient } from '../../core/context/AuthClientContext';
import LoginClientModal from './LoginClientModal';
import RegisterClientModal from './RegisterClientModal';

const Header = ({ theme, offer: offerProp, activeId: activeIdProp }) => {
  const offer = offerProp || clientConfig.offer || 'Essentielle';

  const texts = clientConfig?.texts || {};
  const headerTexts = texts?.header || {};
  const navTexts = texts?.nav || {};
  const logo = texts?.nav?.logo || null;

  // ✅ IDs cohérents avec tes sections actuelles
  const sectionIds = useMemo(() => {
    const ids = ['home', 'about', 'menu', 'contact'];
    if (offer === 'Réservation') ids.splice(3, 0, 'reservation');
    return ids;
  }, [offer]);

  const activeIdFromSpy = useScrollSpy(sectionIds, 120);
  const activeId = activeIdProp || activeIdFromSpy;

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 900);
  const [isScrolled, setIsScrolled] = useState(false);

  // ✅ Géoloc (facultatif)
  const [locationName, setLocationName] = useState(navTexts?.locationUnknown || '');

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  // ✅ Sécurisation Cart/Auth (si App n’embarque pas les Providers)
  const cartContext = typeof useCart === 'function' ? useCart() : {};
  const totalQuantity = cartContext?.totalQuantity || 0;
  const toggleCart = cartContext?.toggleCart || (() => {});

  const authContext = typeof useAuthClient === 'function' ? useAuthClient() : {};
  const isAuthenticated = authContext?.isAuthenticated || false;
  const logout = authContext?.logout || (() => {});
  const user = authContext?.user || null;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    const handleScroll = () => {
      setIsMobileMenuOpen(false);
      setIsScrolled(window.scrollY > 12);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const unknown = navTexts?.locationUnknown || '';
    const notSupported = navTexts?.locationNotSupported || unknown;
    const refused = navTexts?.locationRefused || unknown;

    const fetchLocation = async (lat, lon) => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/location/reverse?lat=${lat}&lon=${lon}`
        );
        if (!response.ok) throw new Error('No location API');
        const data = await response.json();
        setLocationName(data.city || unknown);
      } catch {
        setLocationName(unknown);
      }
    };

    if (!navigator.geolocation) {
      setLocationName(notSupported);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => fetchLocation(pos.coords.latitude, pos.coords.longitude),
      () => setLocationName(refused),
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 600000 }
    );
  }, []);

  const closeMobile = () => setIsMobileMenuOpen(false);

  // ✅ zéro texte en dur : si pas trouvé -> on renvoie l'id
  const labelFor = (id) => headerTexts?.[id] || id;

  const renderLinks = (withLi = false, className = '') =>
    sectionIds.map((id) => {
      const label = labelFor(id);
      const isActive = activeId === id;

      const link = (
        <a
          key={id}
          href={`#${id}`}
          className={`${className} ${isActive ? 'active' : ''}`.trim()}
          onClick={closeMobile}
        >
          {label}
        </a>
      );

      return withLi ? <li key={id}>{link}</li> : link;
    });

  const renderThemeHeader = (classPrefix) => (
    <header className={`${classPrefix}-header`}>
      <div className={`${classPrefix}-container`}>
        <div className={`${classPrefix}-logo`}>{clientConfig.name}</div>
        <nav className={`${classPrefix}-nav ${isMobileMenuOpen ? 'open' : ''}`}>
          {renderLinks(false)}
        </nav>
        <div
          className={`${classPrefix}-menu-toggle`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          role="button"
          tabIndex={0}
          aria-label={navTexts?.openMenu || ''}
          onKeyDown={(e) => e.key === 'Enter' && setIsMobileMenuOpen((s) => !s)}
        >
          ☰
        </div>
      </div>

      <LoginClientModal
        visible={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSwitchToRegister={() => {
          setShowLoginModal(false);
          setShowRegisterModal(true);
        }}
      />
      <RegisterClientModal
        visible={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onSwitchToLogin={() => {
          setShowRegisterModal(false);
          setShowLoginModal(true);
        }}
      />
    </header>
  );

  switch (theme) {
    case 'theme1':
      return (
        <>
          <header className={`theme1-header ${isScrolled ? 'scrolled' : ''}`}>
            <nav className="nav-container">
              <a
                href="#home"
                className="theme1-brand"
                onClick={closeMobile}
                aria-label={navTexts?.ariaHome || ''}
              >
                {logo ? (
                  <img
                    src={logo}
                    alt={navTexts?.logoAlt || ''}
                    className="theme1-logo-img"
                    loading="eager"
                    decoding="async"
                  />
                ) : (
                  <span className="theme1-logo-dot" aria-hidden="true" />
                )}
                <span className="site-title">{clientConfig.name}</span>
              </a>

              {!isMobile && <ul className="nav-links theme1-nav-links">{renderLinks(true)}</ul>}

              <div className="theme1-actions">
                <div className="theme1-location" title={locationName}>
                  <span className="theme1-location-pin" aria-hidden="true">
                    📍
                  </span>
                  <span className="theme1-location-text">{locationName}</span>
                </div>

                {offer === 'Commande' && (
                  <>
                    {isAuthenticated ? (
                      <button className="theme1-btn theme1-btn-ghost" onClick={logout}>
                        {navTexts?.logout || ''}
                      </button>
                    ) : (
                      <button
                        className="theme1-btn theme1-btn-ghost"
                        onClick={() => setShowLoginModal(true)}
                      >
                        {navTexts?.login || ''}
                      </button>
                    )}

                    <button
                      className="theme1-cart"
                      type="button"
                      onClick={toggleCart}
                      aria-label={navTexts?.ariaCart || ''}
                    >
                      🛍️
                      <span
                        className="theme1-cart-badge"
                        aria-label={`${navTexts?.ariaCartCount || ''} ${totalQuantity}`}
                      >
                        {totalQuantity}
                      </span>
                    </button>
                  </>
                )}

                {isMobile && (
                  <button
                    className="burger-button theme1-burger"
                    onClick={() => setIsMobileMenuOpen((s) => !s)}
                    aria-label={navTexts?.openMenu || ''}
                    aria-expanded={isMobileMenuOpen}
                  >
                    ☰
                  </button>
                )}
              </div>
            </nav>

            {isMobile && (
              <div className={`theme1-mobile-panel ${isMobileMenuOpen ? 'open' : ''}`}>
                <ul className="theme1-mobile-links">{renderLinks(true)}</ul>

                <div className="theme1-mobile-cta">
                  <a className="theme1-btn theme1-btn-primary" href="#menu" onClick={closeMobile}>
                    {navTexts?.ctaMenu || ''}
                  </a>
                  {offer === 'Réservation' && (
                    <a
                      className="theme1-btn theme1-btn-ghost"
                      href="#reservation"
                      onClick={closeMobile}
                    >
                      {navTexts?.ctaReservation || ''}
                    </a>
                  )}
                </div>
              </div>
            )}
          </header>

          <LoginClientModal
            visible={showLoginModal}
            onClose={() => setShowLoginModal(false)}
            onSwitchToRegister={() => {
              setShowLoginModal(false);
              setShowRegisterModal(true);
            }}
          />
          <RegisterClientModal
            visible={showRegisterModal}
            onClose={() => setShowRegisterModal(false)}
            onSwitchToLogin={() => {
              setShowRegisterModal(false);
              setShowLoginModal(true);
            }}
          />
        </>
      );

    // ✅ THEME 2 : header premium distinct (plus "night / lounge")
    case 'theme2':
      return (
        <>
          <header className={`theme2-header ${isScrolled ? 'scrolled' : ''}`}>
            <div className="theme2-navbar">
              {/* Brand */}
              <a href="#home" className="theme2-brand" onClick={closeMobile} aria-label={navTexts?.ariaHome || ''}>
                {logo ? (
                  <img
                    src={logo}
                    alt={navTexts?.logoAlt || ''}
                    className="theme2-logo-img"
                    loading="eager"
                    decoding="async"
                  />
                ) : null}
                <div className="theme2-logo">{clientConfig.name}</div>
              </a>

              {/* Nav desktop */}
              {!isMobile && (
                <ul className="theme2-nav-links">
                  {renderLinks(true)}
                </ul>
              )}

              {/* Actions */}
              <div className="theme2-actions">
                {/* Location (theme2: discret, "pro") */}
                <div className="theme2-location" title={locationName}>
                  <span className="theme2-location-dot" aria-hidden="true" />
                  <span className="theme2-location-text">{locationName}</span>
                </div>

                {/* Offer CTAs (optionnel) */}
                <a className="theme2-cta theme2-cta-ghost" href="#menu" onClick={closeMobile}>
                  {navTexts?.ctaMenu || ''}
                </a>

                {offer === 'Réservation' && (
                  <a className="theme2-cta theme2-cta-primary" href="#reservation" onClick={closeMobile}>
                    {navTexts?.ctaReservation || ''}
                  </a>
                )}

                {/* Commande actions */}
                {offer === 'Commande' && (
                  <>
                    {isAuthenticated ? (
                      <button className="theme2-cta theme2-cta-ghost" onClick={logout} type="button">
                        {navTexts?.logout || ''}
                      </button>
                    ) : (
                      <button
                        className="theme2-cta theme2-cta-ghost"
                        onClick={() => setShowLoginModal(true)}
                        type="button"
                      >
                        {navTexts?.login || ''}
                      </button>
                    )}

                    <button
                      className="theme2-cart"
                      type="button"
                      onClick={toggleCart}
                      aria-label={navTexts?.ariaCart || ''}
                      title={navTexts?.ariaCart || ''}
                    >
                      🛍️
                      <span className="theme2-cart-badge" aria-label={`${navTexts?.ariaCartCount || ''} ${totalQuantity}`}>
                        {totalQuantity}
                      </span>
                    </button>
                  </>
                )}

                {/* Burger */}
                {isMobile && (
                  <button
                    className="theme2-burger"
                    onClick={() => setIsMobileMenuOpen((s) => !s)}
                    aria-label={navTexts?.openMenu || ''}
                    aria-expanded={isMobileMenuOpen}
                    type="button"
                  >
                    ☰
                  </button>
                )}
              </div>
            </div>

            {/* Mobile panel (distinct de theme1) */}
            {isMobile && (
              <div className={`theme2-mobile-panel ${isMobileMenuOpen ? 'open' : ''}`}>
                <ul className="theme2-mobile-links">
                  {renderLinks(true)}
                </ul>

                <div className="theme2-mobile-actions">
                  <a className="theme2-cta theme2-cta-ghost" href="#menu" onClick={closeMobile}>
                    {navTexts?.ctaMenu || ''}
                  </a>
                  {offer === 'Réservation' && (
                    <a className="theme2-cta theme2-cta-primary" href="#reservation" onClick={closeMobile}>
                      {navTexts?.ctaReservation || ''}
                    </a>
                  )}

                  {offer === 'Commande' && (
                    <>
                      {isAuthenticated ? (
                        <button className="theme2-cta theme2-cta-ghost" onClick={logout} type="button">
                          {navTexts?.logout || ''}
                        </button>
                      ) : (
                        <button
                          className="theme2-cta theme2-cta-ghost"
                          onClick={() => setShowLoginModal(true)}
                          type="button"
                        >
                          {navTexts?.login || ''}
                        </button>
                      )}

                      <button className="theme2-cart" type="button" onClick={toggleCart} aria-label={navTexts?.ariaCart || ''}>
                        🛍️
                        <span className="theme2-cart-badge-inline">{totalQuantity}</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </header>

          <LoginClientModal
            visible={showLoginModal}
            onClose={() => setShowLoginModal(false)}
            onSwitchToRegister={() => {
              setShowLoginModal(false);
              setShowRegisterModal(true);
            }}
          />
          <RegisterClientModal
            visible={showRegisterModal}
            onClose={() => setShowRegisterModal(false)}
            onSwitchToLogin={() => {
              setShowRegisterModal(false);
              setShowLoginModal(true);
            }}
          />
        </>
      );

    case 'theme3':
      // theme3 inchangé (tu peux le retravailler plus tard)
      return (
        <header className={`${theme}-header`}>
          <div className={`${theme}-navbar`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {logo && (
                <img
                  src={logo}
                  alt="logo"
                  style={{ height: '40px', objectFit: 'contain', maxWidth: '120px' }}
                />
              )}
              <div className={`${theme}-logo`}>{clientConfig.name}</div>
            </div>
            {isMobile && (
              <button
                className={`${theme}-burger`}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                ☰
              </button>
            )}
            <ul
              className={`${theme}-nav-links ${
                isMobile ? (isMobileMenuOpen ? 'open' : 'hidden') : ''
              }`}
            >
              {renderLinks(true)}
            </ul>
          </div>
          <LoginClientModal
            visible={showLoginModal}
            onClose={() => setShowLoginModal(false)}
            onSwitchToRegister={() => {
              setShowLoginModal(false);
              setShowRegisterModal(true);
            }}
          />
          <RegisterClientModal
            visible={showRegisterModal}
            onClose={() => setShowRegisterModal(false)}
            onSwitchToLogin={() => {
              setShowRegisterModal(false);
              setShowLoginModal(true);
            }}
          />
        </header>
      );

    case 'theme4':
    case 'theme5':
      return (
        <>
          <header className={`${theme}-header`}>
            <div
              className={`${theme}-container`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {logo && (
                  <img
                    src={logo}
                    alt={navTexts?.logoAlt || ''}
                    style={{ height: '40px', objectFit: 'contain', maxWidth: '120px' }}
                  />
                )}
                <div className={`${theme}-logo`}>{clientConfig.name}</div>
              </div>
              <nav className={`${theme}-nav ${isMobileMenuOpen ? 'open' : ''}`}>{renderLinks(false)}</nav>
              <div
                className={`${theme}-menu-toggle`}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={navTexts?.openMenu || ''}
              >
                ☰
              </div>
            </div>
          </header>
          <LoginClientModal
            visible={showLoginModal}
            onClose={() => setShowLoginModal(false)}
            onSwitchToRegister={() => {
              setShowLoginModal(false);
              setShowRegisterModal(true);
            }}
          />
          <RegisterClientModal
            visible={showRegisterModal}
            onClose={() => setShowRegisterModal(false)}
            onSwitchToLogin={() => {
              setShowRegisterModal(false);
              setShowLoginModal(true);
            }}
          />
        </>
      );

    case 'theme6':
      return (
        <>
          <header className="theme6-header">
            <div className="container-fluid d-flex flex-wrap justify-content-between align-items-center py-3 px-4">
              <div className="d-flex align-items-center gap-3">
                {logo && (
                  <img
                    src={logo}
                    alt={navTexts?.logoAlt || ''}
                    style={{ height: '40px', objectFit: 'contain', maxWidth: '120px' }}
                  />
                )}
                <div className="theme6-logo">{clientConfig.name}</div>
              </div>

              {isMobile && (
                <button
                  className="theme6-burger"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  aria-label={navTexts?.openMenu || ''}
                >
                  <i className="bi bi-list"></i>
                </button>
              )}

              <nav className={`theme6-nav ${isMobile ? (isMobileMenuOpen ? 'open' : 'd-none') : 'd-flex'}`}>
                <ul className="nav gap-3 align-items-center m-0">
                  <li>
                    <a href="#about">{headerTexts?.about || 'about'}</a>
                  </li>
                  <li>
                    <a href="#menu">{headerTexts?.menu || 'menu'}</a>
                  </li>
                  {offer === 'Réservation' && (
                    <li>
                      <a href="#reservation">{headerTexts?.reservation || 'reservation'}</a>
                    </li>
                  )}
                  <li>
                    <a href="#contact">{headerTexts?.contact || 'contact'}</a>
                  </li>
                </ul>
              </nav>

              <div className={`theme6-actions d-flex align-items-center gap-3 ${isMobile ? 'mt-3 w-100 flex-column align-items-center' : ''}`}>
                {offer === 'Commande' ? (
                  isAuthenticated ? (
                    <>
                      <a href="/account" className="btn btn-outline-theme">
                        👋 {user?.name || user?.firstname || navTexts?.myAccount || ''}
                      </a>
                      <a href="/account" className="btn btn-outline-theme">
                        {navTexts?.myOrders || ''}
                      </a>
                      <button onClick={logout} className="btn btn-outline-theme">
                        {navTexts?.logout || ''}
                      </button>
                    </>
                  ) : (
                    <button onClick={() => setShowLoginModal(true)} className="btn btn-outline-theme">
                      {navTexts?.login || ''}
                    </button>
                  )
                ) : null}

                {offer === 'Commande' && (
                  <div
                    className="theme6-cart position-relative"
                    role="button"
                    tabIndex={0}
                    aria-label={navTexts?.ariaCart || ''}
                    onClick={toggleCart}
                    onKeyDown={(e) => e.key === 'Enter' && toggleCart()}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className="bi bi-bag-fill" style={{ fontSize: '1.5rem' }}></i>
                    <span className="badge bg-theme position-absolute top-0 start-100 translate-middle rounded-pill">
                      {totalQuantity}
                    </span>
                  </div>
                )}

                <div className="theme6-location d-flex align-items-center gap-1">
                  <i className="bi bi-geo-alt-fill"></i>
                  <span>{locationName}</span>
                </div>
              </div>
            </div>
          </header>

          <LoginClientModal
            visible={showLoginModal}
            onClose={() => setShowLoginModal(false)}
            onSwitchToRegister={() => {
              setShowLoginModal(false);
              setShowRegisterModal(true);
            }}
          />
          <RegisterClientModal
            visible={showRegisterModal}
            onClose={() => setShowRegisterModal(false)}
            onSwitchToLogin={() => {
              setShowRegisterModal(false);
              setShowLoginModal(true);
            }}
          />
        </>
      );

    default:
      return renderThemeHeader(theme);
  }
};

export default Header;
